'use client';

import { c as _c } from "react/compiler-runtime";
import { ShareIcon } from '@vidstack/react/icons';
import { useEffect, useState } from 'react';
import { ClipboardIcon, EmailIcon, FacebookIcon, LinkedinIcon, TwitterIcon } from '@shared/ui-components/svgs';
import { ToastType } from '@shared/ui-components/toast/constants/constants';
import { useGlobalToastContext } from '@shared/ui-components/toast/context/global-toast-context';
import { GTM_EVENT_NAME_LEARN_SHARE } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { Button } from '../button/button';
import { DropdownMenu } from '../dropdown-menu/dropdown-menu';
import { type ShareDropdownCmsContent } from './get-share-dropdown-cms-content';
import { type ShareDropdownProps } from './share-dropdown';
import styles from './share-dropdown.module.scss';
type ShareDropdownClientProps = ShareDropdownProps & {
  cmsContent: ShareDropdownCmsContent;
};
const handleGTMShareEvent = (label?: string) => sendGtmEvent({
  eventName: GTM_EVENT_NAME_LEARN_SHARE,
  label: label || GTM_EVENT_NAME_LEARN_SHARE
});
export const ShareDropdownClient = t0 => {
  const $ = _c(42);
  const {
    cmsContent,
    dataTestId,
    menuPlacement,
    title,
    url
  } = t0;
  const [hasShareSupport, setHasShareSupport] = useState(undefined);
  const [resourceUrl, setResourceUrl] = useState("");
  const [resourceTitle, setResourceTitle] = useState("");
  const {
    addToast
  } = useGlobalToastContext();
  let t1;
  if ($[0] !== addToast || $[1] !== cmsContent.close || $[2] !== cmsContent.somethingWentWrong) {
    t1 = () => addToast({
      closeButtonLabel: cmsContent.close,
      description: cmsContent.somethingWentWrong,
      type: ToastType.ERROR
    }, {
      timeout: 0
    });
    $[0] = addToast;
    $[1] = cmsContent.close;
    $[2] = cmsContent.somethingWentWrong;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const showUnknownErrorToast = t1;
  const shareData = {
    title: resourceTitle,
    url: resourceUrl
  };
  const t2 = `https://www.linkedin.com/sharing/share-offsite/?url=${resourceUrl}`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LinkedinIcon />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== cmsContent.shareOnLinkedIn || $[6] !== t2) {
    t4 = {
      children: cmsContent.shareOnLinkedIn,
      href: t2,
      Icon: t3,
      id: "LinkedIn",
      onAction: _temp,
      rel: "noopener noreferrer",
      target: "_blank"
    };
    $[5] = cmsContent.shareOnLinkedIn;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `https://twitter.com/intent/tweet?url=${resourceUrl}`;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TwitterIcon />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== cmsContent.shareOnX || $[10] !== t5) {
    t7 = {
      children: cmsContent.shareOnX,
      href: t5,
      Icon: t6,
      id: "Twitter",
      onAction: _temp2,
      rel: "noopener noreferrer",
      target: "_blank"
    };
    $[9] = cmsContent.shareOnX;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `https://www.facebook.com/sharer/sharer.php?u=${resourceUrl}`;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FacebookIcon />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== cmsContent.shareOnFacebook || $[14] !== t8) {
    t10 = {
      children: cmsContent.shareOnFacebook,
      href: t8,
      Icon: t9,
      id: "Facebook",
      onAction: _temp3,
      rel: "noopener noreferrer",
      target: "_blank",
      withSeparatorAfter: true
    };
    $[13] = cmsContent.shareOnFacebook;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ClipboardIcon />;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== addToast || $[18] !== cmsContent.close || $[19] !== cmsContent.linkCopiedSuccessfully || $[20] !== resourceUrl || $[21] !== showUnknownErrorToast) {
    t12 = async () => {
      try {
        await window.navigator.clipboard.writeText(resourceUrl);
        addToast({
          closeButtonLabel: cmsContent.close,
          description: cmsContent.linkCopiedSuccessfully,
          type: ToastType.SUCCESS
        }, {
          timeout: 5000
        });
      } catch {
        showUnknownErrorToast();
      }
    };
    $[17] = addToast;
    $[18] = cmsContent.close;
    $[19] = cmsContent.linkCopiedSuccessfully;
    $[20] = resourceUrl;
    $[21] = showUnknownErrorToast;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== cmsContent.copyLink || $[24] !== t12) {
    t13 = {
      children: cmsContent.copyLink,
      Icon: t11,
      id: "clipboard",
      onAction: t12
    };
    $[23] = cmsContent.copyLink;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = `mailto:?subject=${encodeURIComponent(resourceTitle)}&body=${resourceUrl}`;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <EmailIcon />;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== cmsContent.emailResource || $[28] !== t14) {
    t16 = {
      children: cmsContent.emailResource,
      href: t14,
      Icon: t15,
      id: "email"
    };
    $[27] = cmsContent.emailResource;
    $[28] = t14;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t10 || $[31] !== t13 || $[32] !== t16 || $[33] !== t4 || $[34] !== t7) {
    t17 = [t4, t7, t10, t13, t16];
    $[30] = t10;
    $[31] = t13;
    $[32] = t16;
    $[33] = t4;
    $[34] = t7;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const menuItems = t17;
  const handleShare = async () => {
    await window.navigator.share(shareData).then(_temp4).catch(error => {
      if (error.name !== "AbortError") {
        showUnknownErrorToast();
      }
    });
  };
  let t18;
  let t19;
  if ($[36] !== title || $[37] !== url) {
    t18 = () => {
      setResourceUrl(url || window.location.href);
      setResourceTitle(title || window.document.title);
      setHasShareSupport(Boolean(window.navigator.share));
    };
    t19 = [title, url];
    $[36] = title;
    $[37] = url;
    $[38] = t18;
    $[39] = t19;
  } else {
    t18 = $[38];
    t19 = $[39];
  }
  useEffect(t18, t19);
  if (hasShareSupport === undefined) {
    let t20;
    if ($[40] !== dataTestId) {
      t20 = <Button dataTestId={dataTestId} Icon={ShareIcon} isLoading={true} />;
      $[40] = dataTestId;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    return t20;
  }
  return hasShareSupport && window.navigator.canShare(shareData) ? <button aria-label={cmsContent.shareResourceMenuTrigger} className={styles.trigger} data-testid={dataTestId} onClick={handleShare}><ShareIcon /></button> : <DropdownMenu items={menuItems} menuClassName={styles.menu} menuPlacement={menuPlacement} triggerDataTestId={dataTestId} TriggerIcon={<ShareIcon />} triggerLabel={cmsContent.shareResourceMenuTrigger} />;
};
function _temp() {
  return handleGTMShareEvent("LinkedIn");
}
function _temp2() {
  return handleGTMShareEvent("Twitter");
}
function _temp3() {
  return handleGTMShareEvent("Facebook");
}
function _temp4() {
  return handleGTMShareEvent();
}